import { useMemo } from "react"
import { useSelector } from "react-redux"
import { usePortfolios } from "resources/portfolios"
import { useAccountAlias, useIsIntradayEnabled, useIsOpPro } from "resources/account"
import { getPortfolioMenuItems } from "modules/portfolio-root/selectors/menu"
import { getModels } from "resources/models"
import { combineTanStackStatuses, OK, ResourceResult } from "helpers/data-statuses"
import { useResourcePermission } from "hooks/permissions"
import { useIsResourceFavorite } from "hooks/favorites"
import { matchATForecastByName } from "modules/portfolio-root/helpers"
import { filterATForecasts, useForecasts } from "resources/forecasts"
import { SelectedPortfolio } from "helpers/types/instruments/selected"

export function usePortfolioMenuItems(instrument: SelectedPortfolio, onSuccess: () => void, onDelete: () => void): ResourceResult<unknown> {
  const account = useAccountAlias()
  const models = useSelector(getModels)
  const intradayAccountEnabled = useIsIntradayEnabled()
  const isOpPro = useIsOpPro()
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [forecasts, forecastsStatus] = useForecasts()
  const [resourcePermission, resourcePermissionStatus] = useResourcePermission(instrument.type, instrument.id)
  const [isPortfolioFavorite, favoritesStatus] = useIsResourceFavorite(instrument.id)

  const atForecasts = useMemo(() =>
    filterATForecasts(forecasts),
  [forecasts])

  const combinedStatus = useMemo(() =>
    combineTanStackStatuses([portfoliosStatus, resourcePermissionStatus, favoritesStatus, forecastsStatus]),
  [portfoliosStatus, resourcePermissionStatus, favoritesStatus, forecastsStatus],
  )

  return useMemo((): ResourceResult<({
    id: string
    label: string
    action?: undefined
    to?: string
    className?: string
  } | {
    id: string
    label: string
    action?: (dispatch: unknown, navigate?: unknown) => void
    to?: undefined
    className?: string
  })[] | null> => {
    if (combinedStatus !== OK) {
      return [null, combinedStatus]
    }

    const portfolioData = portfolios?.find(portfolio => portfolio.id === instrument.id)
    if (!portfolioData) {
      return [null, OK]
    }

    // Create portfolio object with metadata needed for menu items
    const portfolioWithMeta = {
      ...portfolioData,
      alias: portfolioData.alias || portfolioData.shortId,
      isFavorite: isPortfolioFavorite,
      permissionLevel: resourcePermission || "READ",
      isLinkedToAlphaTheory: matchATForecastByName(portfolioData.name, atForecasts),
    }

    const result = getPortfolioMenuItems(account, portfolioWithMeta, models, intradayAccountEnabled, isOpPro, { onSuccess, onDelete })

    return [result, combinedStatus]
  }, [instrument, portfolios, account, isPortfolioFavorite, resourcePermission, models, intradayAccountEnabled, isOpPro, atForecasts, combinedStatus, onSuccess, onDelete])
}
